<script setup>
const pollingTimeout = ref(null);
const POLLING_INTERVAL_MS = 3000; // Polling interval in milliseconds
const POLLING_TIMEOUT_MS = 40 * 60 * 1000; // Timeout after 40 minutes

const LOADING_MESSAGES = Object.freeze({
  default: 'Please wait; do not refresh or close the page.',
  processing:
    'Almost there! Your request is being processed. Please do not refresh or close the page.',
  connectionLost: 'Connection lost. Please refresh the page to continue.',
  subscriptionRejected: 'Something went wrong.',
  resumingInquiryError:
    'Something went wrong. Please refresh the page and try again.',
});

import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { usePersona } from '@/composables/usePersona';
import { useActionCable } from '@/composables/useActionCable';
import LoadingIdvSignUp from '@/components/LoadingIdvSignUp.vue';
import LoadingIDVModal from '@/components/modals/LoadingIDVModal.vue';
const props = defineProps({
  preventRedirects: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const loadingMessage = ref(LOADING_MESSAGES.default);
const store = useStore();
const route = useRoute();
const router = useRouter();
const persona = usePersona();
const { subscribeToChannel, disconnectChannel } = useActionCable(`/cable`);

const pollingInterval = ref(null);

const carrierId = computed(
  //Including the verification of the carrier ID within the company. This case is specifically
  // necessary for the Inbound flow since the requested_carrier_id is null.
  () =>
    store.state.users.currentUser?.requested_carrier_id ||
    store.state.users.currentUser?.company?.carrier_id
);

const underReviewMsg =
  'Your identity verification is under review. </br> Please reach out to <a href="mailto:security@highway.com" class="text-yellow-500 underline">security@highway.com</a>.';

const enforcedUnderReview = computed(
  () =>
    store.state.users.featureFlags?.display_under_review_idv_message?.enabled
);
watchEffect(() => {
  if (enforcedUnderReview.value) {
    loadingMessage.value = underReviewMsg;
  }
});
const companyType = computed(
  () => store.state.users.currentUser.company?.company_type
);
const useSocketForIDV = computed(() => {
  const getFeatureFlag = store.getters['users/getFeatureFlag'];
  return getFeatureFlag
    ? getFeatureFlag('use_sockets_for_idv_flow')?.enabled
    : false;
});

const latestInquiry = computed(() => store.getters['users/getLatestInquiry']);

const isOnIDVRoute = computed(() => {
  return router.currentRoute.value.name === 'IdentityVerification';
});

onMounted(async () => {
  try {
    console.log('🎆 Mount IDV flow');
    await store.dispatch(
      'identityVerification/getCustomFields',
      carrierId.value
    );
    await setupPersonaClient();
  } catch (error) {
    console.error('Failed to set up Persona/ActionCable:', error);
  }
});
/**
 * Sets up the Persona client depending on whether we need to create a new inquiry
 * or resume an existing one.
 */
const setupPersonaClient = async () => {
  const inquiry = store.getters['users/getLatestInquiry'];

  // Once an inquiry is approved, we update the user's record with the timestamp in last_verified_identity_at.
  // If a user is approved, we don't need to send them through the IDV flow again.
  const isUserIdentityVerified =
    store.state.currentUser?.last_verified_identity_at;

  if (
    inquiry?.status === 'approved' ||
    inquiry?.status === 'declined' ||
    isUserIdentityVerified
  ) {
    console.log('✅ Inquiry is either approved or declined, no action needed');
    return;
  }
  const getInquiry = await store.dispatch('users/getInquiry');

  persona.initializePersonaClientWithInquiry(
    getInquiry?.data?.external_id,
    getInquiry?.data?.session_token
  );

  // Only subscribe to Action Cable if we're creating or resuming an inquiry
  if (useSocketForIDV.value) {
    subscribeToActionCable();
  } else {
    startPolling();
  }
};

const startPolling = () => {
  console.log('🔄 Starting polling for inquiries...');

  pollingTimeout.value = setTimeout(() => {
    console.log('⏳ Polling timed out, stopping polling...');
    stopPolling();
    loadingMessage.value = LOADING_MESSAGES.resumingInquiryError;
  }, POLLING_TIMEOUT_MS);

  pollingInterval.value = setInterval(async () => {
    console.log('🔄 Polling inquiries...');
    await store.dispatch(
      'users/pollInquiries',
      store.state.users.currentUser.id
    );
    const latestStatus = latestInquiry.value?.status;

    if (['approved', 'declined', 'under_review'].includes(latestStatus)) {
      stopPolling();
      handleInquiryStatus(latestStatus);
    }
  }, POLLING_INTERVAL_MS);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
  if (pollingTimeout.value) {
    clearTimeout(pollingTimeout.value);
    pollingTimeout.value = null;
  }
};

onUnmounted(() => {
  stopPolling();
});

const handleInquiryStatus = async (status) => {
  console.log('🔍 Handling inquiry status:', status);

  // Mobile App redirect
  if (window.ReactNativeWebView?.postMessage) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'Navigation',
        screen: 'OnboardingScreen',
      })
    );

    return;
  }

  if (status === 'declined' || status === 'under_review') {
    return router.push({ name: 'VerificationFail' });
  }

  if (route.name == 'Protect Your Identity Exchange') {
    await store.dispatch('users/checkLoadBoardAccess');
  }

  props.preventRedirects ? triggerPreferredNameModal() : redirectUser();
};

const subscribeToActionCable = () => {
  subscribeToChannel('UserInquiryChannel', {
    connected: (channelName) => {
      console.log(`✅ Successfully connected to channel: ${channelName}`);
    },
    received: (data, channelName) => {
      console.log(`📥 Data received from channel ${channelName}:`, data);

      loadingMessage.value = LOADING_MESSAGES.processing;

      if (['approved', 'declined'].includes(data.status)) {
        disconnectChannel();
        getUserInquiries();
        // We have two flows:
        // 1. When the user is in the SignUp process.
        // 2. When the user is already logged in.
        // Redirects are used in the SignUp flow,
        // but for logged-in users, we should mutate the IdentityVerification state
        // to activate the modal in App.vue (PreferredName or Email Verification).

        if (data.status === 'declined') {
          return router.push({ name: 'VerificationFail' });
        }
        props.preventRedirects ? triggerPreferredNameModal() : redirectUser();
      }
    },
    disconnected: (channelName) => {
      loadingMessage.value = LOADING_MESSAGES.connectionLost;
      console.log(`🔌 Disconnected from channel: ${channelName}`);
    },
    rejected: (channelName) => {
      loadingMessage.value = LOADING_MESSAGES.subscriptionRejected;
      console.error(`❌ Subscription to ${channelName} was rejected.`);
    },
  });
};

const getUserInquiries = async () => {
  await store.dispatch(
    'users/getUserInquiries',
    store.state.users.currentUser.id
  );
  await store.dispatch('users/serverRefreshCurrentUser');
};

const triggerPreferredNameModal = async () => {
  await store.dispatch('users/serverRefreshCurrentUser');
  store.commit('identityVerification/setShowIDVFlow', false);
  store.commit('identityVerification/setPreferredNameModal', true);
};
const redirectUser = () => {
  // 1. If the user has a company type (likely in the login flow),
  // redirect to the Preferred Name page or, if the user is a manual carrier: coming
  // from intrastate, intra canadian, intra mex, intra europe, we should let go to preferred name.

  if (
    companyType.value ||
    store.state.signUpData?.manualCarrier?.legalName?.length > 0
  ) {
    return router.push({ name: 'PreferredName' });
  }

  // 2. If neither of the above conditions apply, redirect to the CompanyType page (likely part of the sign-up flow)
  router.push({ name: 'CompanyType' });
};
</script>

<template>
  <section>
    <LoadingIdvSignUp
      v-if="isOnIDVRoute"
      :loading-message="loadingMessage"
    />
    <LoadingIDVModal
      v-else
      :loading-message="loadingMessage"
    />
  </section>
</template>
<style scoped>
@keyframes blink {
  0% {
    opacity: 1;
  }

  33% {
    opacity: 0;
  }

  66% {
    opacity: 1;
  }
}

.dots::after {
  content: '.';
  animation: blink 1s steps(5, end) infinite;
}
</style>
